// Self-serve PSP credential entry. Owner can create/rotate; developer
// + viewer can read metadata. Admin uses /admin/merchants/.../gateways
// for centralised onboarding; this page is the merchant-facing path.

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  ApiError,
  merchantAuth,
  merchantGateways,
  type MerchantGatewayOut,
} from '@/api/client';
import type { PspCode } from '@/api/types';
import { PspLogo } from '@/features/merchant/psp-brand';
import { ALL_PSPS, PSP_SCHEMAS } from '@/features/merchant/psp-schemas';
import { ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const Route = createFileRoute('/m/gateways')({
  component: MerchantGatewaysPage,
});

function MerchantGatewaysPage() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ['merchant-me'], queryFn: merchantAuth.me });
  const list = useQuery({ queryKey: ['merchant-gateways'], queryFn: merchantGateways.list });
  const [editPsp, setEditPsp] = useState<PspCode | null>(null);

  const isOwner = me.data?.role === 'owner';

  const byPsp: Record<string, MerchantGatewayOut | undefined> = Object.fromEntries(
    (list.data ?? []).map((g) => [g.psp_code, g]),
  );

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('merchant.gateways.title', { defaultValue: 'PSP gateways' })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('merchant.gateways.subtitle', {
            defaultValue:
              'Configure your Sadad / Moamalat / Mobicash / T-Lync credentials. Credentials are encrypted at rest and never displayed back.',
          })}
        </p>
      </header>

      {list.isError && <ErrorBox message={t('common.error')} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {ALL_PSPS.map((psp) => (
          <Card key={psp}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2.5">
                <PspLogo psp={psp} size="lg" />
                <span className="capitalize">{psp}</span>
              </CardTitle>
              <CardDescription>
                {byPsp[psp]?.active
                  ? t('merchant.gateways.configured', {
                      defaultValue: 'Configured · v{{v}}',
                      v: byPsp[psp]!.version,
                    })
                  : byPsp[psp]
                    ? t('merchant.gateways.inactive', { defaultValue: 'Inactive' })
                    : t('merchant.gateways.not_configured', { defaultValue: 'Not configured' })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-1">
                {byPsp[psp]?.payload_keys.map((k) => (
                  <Badge key={k} variant="secondary" className="text-[10px]">
                    {k}
                  </Badge>
                ))}
              </div>
              <Button
                size="sm"
                variant={byPsp[psp] ? 'outline' : 'default'}
                disabled={!isOwner}
                onClick={() => setEditPsp(psp)}
              >
                {byPsp[psp]
                  ? t('merchant.gateways.rotate', { defaultValue: 'Update credentials' })
                  : t('merchant.gateways.configure', { defaultValue: 'Configure' })}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {editPsp && (
        <UpsertGatewayDialog psp={editPsp} onClose={() => setEditPsp(null)} />
      )}
    </div>
  );
}

function UpsertGatewayDialog({ psp, onClose }: { psp: PspCode; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const def = PSP_SCHEMAS[psp];
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(def.fields.map((f) => [f.name, String(def.defaults[f.name] ?? '')])),
  );
  const [env, setEnv] = useState<'prod' | 'sandbox' | 'test'>('prod');

  const submit = useMutation({
    mutationFn: () => {
      const parsed = def.schema.parse(
        Object.fromEntries(
          def.fields.map((f) => [
            f.name,
            f.kind === 'number' ? Number(values[f.name]) : values[f.name],
          ]),
        ),
      );
      return merchantGateways.upsert(psp, {
        psp,
        env,
        payload: parsed as Record<string, unknown>,
        active: true,
      });
    },
    onSuccess: () => {
      toast.success(t('merchant.gateways.saved', { defaultValue: 'Gateway saved.' }));
      qc.invalidateQueries({ queryKey: ['merchant-gateways'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit.mutate();
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2.5">
              <PspLogo psp={psp} size="md" />
              <span className="capitalize">
                {t('merchant.gateways.dialog_title', {
                  defaultValue: 'Configure {{psp}}',
                  psp,
                })}
              </span>
            </DialogTitle>
            <DialogDescription>
              {t('merchant.gateways.dialog_hint', {
                defaultValue:
                  'Credentials are encrypted at rest and never returned in plaintext.',
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label>{t('merchant.gateways.env', { defaultValue: 'Environment' })}</Label>
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={env}
              onChange={(e) => setEnv(e.target.value as 'prod' | 'sandbox' | 'test')}
            >
              <option value="prod">prod</option>
              <option value="sandbox">sandbox</option>
              <option value="test">test</option>
            </select>
          </div>

          {def.fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label htmlFor={f.name}>{f.label}</Label>
              <Input
                id={f.name}
                type={f.kind === 'secret' ? 'password' : f.kind === 'number' ? 'number' : 'text'}
                placeholder={f.placeholder}
                value={values[f.name] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
              />
              {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
            </div>
          ))}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="submit" disabled={submit.isPending}>
              {submit.isPending
                ? t('common.saving', { defaultValue: 'Saving…' })
                : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
